import "../../Assets/css/kpi_calculation.css";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export const RTSKPICalculation = ({ kpiKey, kpiData, onClose }) => {
  const open = Boolean(kpiKey);
  if (!kpiKey) return null;

  const formatNum = (num) =>
    Number(Number(num)?.toFixed(2))?.toLocaleString("en-US");

  const KPI_CALCULATION_MAP = {
    totalRecords: {
      title: "Total Records",
      sections: [
        {
          label: "Total Records",
          formula:
            "Processed Orders + Processed Executions + Exceptions Orders + Exceptions Executions + Excluded Orders + Excluded Executions",
          values: (kpis) =>
            `${kpis.validOrds} + ${kpis.validExe} + ${kpis.invalidOrds} + ${kpis.invalidExe} + ${kpis.excludedOrds} + ${kpis.excludedExe}`,
          result: (kpi) => formatNum(kpi.totalRecs),
        },
        {
          label: "Orders",
          formula: "Processed Orders + Exceptions Orders + Excluded Orders",
          values: (kpis) =>
            `${kpis.validOrds} + ${kpis.invalidOrds} + ${kpis.excludedOrds}`,
          result: (kpi) => formatNum(kpi.totalOrds),
        },
        {
          label: "Executions",
          formula:
            "Processed Executions + Exceptions Executions + Excluded Executions",
          values: (kpis) =>
            `${kpis.validExe} + ${kpis.invalidExe} + ${kpis.excludedExe}`,
          result: (kpi) => formatNum(kpi.totalExe),
        },
      ],
    },
    processedRecords: {
      title: "Processed Records",
      sections: [
        {
          label: "Processed Records",
          formula: "Processed Orders + Processed Executions",
          values: (kpis) => `${kpis.validOrds} + ${kpis.validExe}`,
          result: (kpi) => formatNum(kpi.valid),
        },
        {
          label: "Orders",
          formula: "Processed Orders",
          values: (kpis) => `${kpis.validOrds}`,
          result: (kpi) => formatNum(kpi.validOrds),
        },
        {
          label: "Executions",
          formula: "Processed Executions",
          values: (kpis) => `${kpis.validExe}`,
          result: (kpi) => formatNum(kpi.validExe),
        },
        {
          label: "% of Total",
          formula:
            "((Processed Orders + Processed Executions) / (Processed Orders + Processed Executions + Exceptions Orders + Exceptions Executions + Excluded Orders + Excluded Executions)) × 100",
          values: (kpis) =>
            `((${kpis.validOrds} + ${kpis.validExe}) / (${kpis.validOrds} + ${kpis.validExe} + ${kpis.invalidOrds} + ${kpis.invalidExe} + ${kpis.excludedOrds} + ${kpis.excludedExe})) * 100`,
          result: (kpi) => `${formatNum(kpi.validPctOfTotal)}%`,
        },
      ],
    },
    exceptionsRecords: {
      title: "Exceptions",
      sections: [
        {
          label: "Exceptions",
          formula: "Exceptions Orders + Exceptions Executions",
          values: (kpis) => `${kpis.invalidOrds} + ${kpis.invalidExe}`,
          result: (kpi) => formatNum(kpi.invalid),
        },
        {
          label: "Orders",
          formula: "Exceptions Orders",
          values: (kpis) => `${kpis.invalidOrds}`,
          result: (kpi) => formatNum(kpi.invalidOrds),
        },
        {
          label: "Executions",
          formula: "Exceptions Executions",
          values: (kpis) => `${kpis.invalidExe}`,
          result: (kpi) => formatNum(kpi.invalidExe),
        },
        {
          label: "% of Total",
          formula:
            "((Exceptions Orders + Exceptions Executions) / (Processed Orders + Processed Executions + Exceptions Orders + Exceptions Executions + Excluded Orders + Excluded Executions)) × 100",
          values: (kpis) =>
            `((${kpis.invalidOrds} + ${kpis.invalidExe}) / (${kpis.validOrds} + ${kpis.validExe} + ${kpis.invalidOrds} + ${kpis.invalidExe} + ${kpis.excludedOrds} + ${kpis.excludedExe})) * 100`,
          result: (kpi) => `${formatNum(kpi.invalidPctOfTotal)}%`,
        },
      ],
    },
    excludedRecords: {
      title: "Excluded Records",
      sections: [
        {
          label: "Excluded Records",
          formula: "Excluded Orders + Excluded Executions",
          values: (kpis) => `${kpis.excludedOrds} + ${kpis.excludedExe}`,
          result: (kpi) => formatNum(kpi.excluded),
        },
        {
          label: "Orders",
          formula: "Excluded Orders",
          values: (kpis) => `${kpis.excludedOrds}`,
          result: (kpi) => formatNum(kpi.excludedOrds),
        },
        {
          label: "Executions",
          formula: "Excluded Executions",
          values: (kpis) => `${kpis.excludedExe}`,
          result: (kpi) => formatNum(kpi.excludedExe),
        },
        {
          label: "% of Total",
          formula:
            "((Excluded Orders + Excluded Executions) / (Processed Orders + Processed Executions + Exceptions Orders + Exceptions Executions + Excluded Orders + Excluded Executions)) × 100",
          values: (kpis) =>
            `((${kpis.excludedOrds} + ${kpis.excludedExe}) / (${kpis.validOrds} + ${kpis.validExe} + ${kpis.invalidOrds} + ${kpis.invalidExe} + ${kpis.excludedOrds} + ${kpis.excludedExe})) * 100`,
          result: (kpi) => `${formatNum(kpi.excludedPctOfTotal)}%`,
        },
      ],
    },
    accuracyPercentage: {
      title: "Accuracy %",
      sections: [
        {
          label: "Accuracy",
          formula:
            "((Processed Orders + Processed Executions) / (Processed Orders + Processed Executions + Exceptions Orders + Exceptions Executions)) × 100",
          values: (kpis) =>
            `((${kpis.validOrds} + ${kpis.validExe}) / (${kpis.validOrds} + ${kpis.validExe} + ${kpis.invalidOrds} + ${kpis.invalidExe})) * 100`,
          result: (kpi) => `${formatNum(kpi.accuracyPct)}%`,
        },
        {
          label: "Orders",
          formula:
            "(Processed Orders / (Processed Orders + Exceptions Orders + Excluded Orders)) × 100",
          values: (kpis) =>
            `(${kpis.validOrds} / (${kpis.validOrds} + ${kpis.invalidOrds} + ${kpis.excludedOrds})) * 100`,
          result: (kpi) => `${formatNum(kpi.accuracyOrdsPct)}%`,
        },
        {
          label: "Executions",
          formula:
            "(Processed Executions / (Processed Orders + Processed Executions + Exceptions Orders + Exceptions Executions)) × 100",
          values: (kpis) =>
            `(${kpis.validExe} / (${kpis.validExe} + ${kpis.invalidExe} + ${kpis.excludedExe})) * 100`,
          result: (kpi) => `${formatNum(kpi.accuracyExePct)}%`,
        },
      ],
    },
    errorPercentage: {
      title: "Error %",
      sections: [
        {
          label: "Error %",
          formula:
            "((Exceptions Orders + Exceptions Executions) / (Exceptions Orders + Exceptions Executions + Processed Orders + Processed Executions)) × 100",
          values: (kpis) =>
            `((${kpis.invalidOrds} + ${kpis.invalidExe}) / (${kpis.invalidOrds} + (${kpis.invalidExe} + ${kpis.validOrds} + ${kpis.validExe})) * 100`,
          result: (kpi) => `${formatNum(kpi.errorPct)}%`,
        },
        {
          label: "Orders",
          formula:
            "(Exceptions Orders / (Processed Orders + Exceptions Orders + Excluded Orders)) × 100",
          values: (kpis) =>
            `(${kpis.invalidOrds} / (${kpis.validOrds} + ${kpis.invalidOrds} + ${kpis.excludedOrds})) * 100`,
          result: (kpi) => `${formatNum(kpi.errorOrdsPct)}%`,
        },
        {
          label: "Executions",
          formula:
            "(Exceptions Executions / (Processed Executions + Exceptions Executions + Excluded Executions)) × 100",
          values: (kpis) =>
            `(${kpis.invalidExe} / (${kpis.validExe} + ${kpis.invalidExe} + ${kpis.excludedExe})) * 100`,
          result: (kpi) => `${formatNum(kpi.errorExePct)}%`,
        },
      ],
    },
    duplicateRecords: {
      title: "Duplicate",
      sections: [
        {
          label: "Orders",
          formula: "Duplicate Orders + Duplicate Executions",
          values: (kpis) =>
            `${kpis.duplicateOrdsPct} + ${kpis.duplicateExePct}`,
          result: (kpi) => formatNum(kpi.duplicateRecords),
        },
        {
          label: "Orders",
          formula: "Duplicate Orders",
          values: (kpis) => `${kpis.duplicateOrdsPct}`,
          result: (kpi) => formatNum(kpi.duplicateOrdsPct),
        },
        {
          label: "Executions",
          formula: "Duplicate Executions",
          values: (kpis) => `${kpis.duplicateExePct}`,
          result: (kpi) => formatNum(kpi.duplicateExePct),
        },
        {
          label: "% of Total",
          formula:
            "((Duplicate Orders + Duplicate Executions) / (Processed Orders + Processed Executions + Exceptions Orders + Exceptions Executions + Excluded Orders + Excluded Executions + Duplicate Orders + Duplicate Executions)) × 100",
          values: (kpis) =>
            `((${kpis.duplicateOrdsPct} + ${kpis.duplicateExePct}) / (${kpis.validOrds} + ${kpis.validExe} + ${kpis.invalidOrds} + ${kpis.invalidExe} + ${kpis.excludedOrds} + ${kpis.excludedExe} + ${kpis.duplicateOrdsPct} + ${kpis.duplicateExePct})) * 100`,
          result: (kpi) => `${formatNum(kpi.duplicatePctOfTotal)}%`,
        },
      ],
    },
  };

  const config = KPI_CALCULATION_MAP[kpiKey];
  if (!config) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          KPI Information
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            No calculation information available for this KPI.
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 4,
        },
      }}
      disableScrollLock
      className="kpi-dialog"
    >
      <DialogTitle sx={{ pr: 5 }}>
        {config.title}
        <IconButton
          onClick={onClose}
          className="kpi-close-btn"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {config.sections.map((section, id) => (
          <Box key={section.label} className="kpi-section">
            {/* Hide label of first section */}
            {id !== 0 && (
              <Typography className="kpi-section-title">
                {section.label}
              </Typography>
            )}

            <Typography className="kpi-formula">{section.formula}</Typography>

            <Box className="kpi-equation">
              {typeof section.values(kpiData) === "string" ? (
                <span>{section.values(kpiData)}</span>
              ) : (
                section.values(kpiData).map((v, i) => (
                  <span key={v.name}>
                    {v.value}
                    {i < section.values(kpiData).length - 1 && (
                      <span className="kpi-plus">+</span>
                    )}
                  </span>
                ))
              )}
            </Box>

            <div className="kpi-result">= {section.result(kpiData)}</div>

            {id < config.sections.length - 1 && (
              <Divider className="kpi-divider" />
            )}
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

// Used for Orders and Executions KPI only
export const BasicKPICalculation = ({
  kpiKey,
  kpiData,
  regulationCode, // R002 || R003
  onClose,
}) => {
  const open = Boolean(kpiKey);
  if (!kpiKey) return null;

  const formatNum = (num) =>
    Number(Number(num)?.toFixed(2))?.toLocaleString("en-US");
  // console.log("regulationCode :", regulationCode);
  const isOrders = regulationCode === "R002";

  const validKey = isOrders ? "validOrds" : "validExe";
  const invalidKey = isOrders ? "invalidOrds" : "invalidExe";
  const excludedKey = isOrders ? "excludedOrds" : "excludedExe";

  const KPI_CALCULATION_MAP = {
    totalRecords: {
      title: "Total Records",
      sections: [
        {
          label: "Total Records",
          formula: "Processed Records + Exceptions + Excluded Records",
          values: (kpis) =>
            `${kpis[validKey]} + ${kpis[invalidKey]} + ${kpis[excludedKey]}`,
          result: (kpi) =>
            formatNum(kpi[validKey] + kpi[invalidKey] + kpi[excludedKey]),
        },
      ],
    },

    processedRecords: {
      title: "Processed Records",
      sections: [
        {
          label: "Processed Records",
          formula: "Processed Records",
          values: (kpis) => `${kpis[validKey]}`,
          result: (kpi) => formatNum(kpi[validKey]),
        },
        {
          label: "% of Total",
          formula:
            "(Processed Records / (Processed Records + Exceptions + Excluded Records)) × 100",
          values: (kpis) =>
            `(${kpis[validKey]} / (${kpis[validKey]} + ${kpis[invalidKey]} + ${kpis[excludedKey]})) * 100`,
          result: (kpi) => `${formatNum(kpi.validPctOfTotal)}%`,
        },
      ],
    },

    exceptionsRecords: {
      title: "Exceptions",
      sections: [
        {
          label: "Exceptions",
          formula: "Exceptions",
          values: (kpis) => `${kpis[invalidKey]}`,
          result: (kpi) => formatNum(kpi[invalidKey]),
        },
        {
          label: "% of Total",
          formula:
            "(Exceptions / (Processed Records + Exceptions + Excluded Records)) × 100",
          values: (kpis) =>
            `(${kpis[invalidKey]} / (${kpis[validKey]} + ${kpis[invalidKey]} + ${kpis[excludedKey]})) * 100`,
          result: (kpi) => `${formatNum(kpi.invalidPctOfTotal)}%`,
        },
      ],
    },

    excludedRecords: {
      title: "Excluded Records",
      sections: [
        {
          label: "Excluded Records",
          formula: "Excluded Records",
          values: (kpis) => `${kpis[excludedKey]}`,
          result: (kpi) => formatNum(kpi[excludedKey]),
        },
        {
          label: "% of Total",
          formula:
            "(Excluded Records / (Processed Records + Exceptions + Excluded Records)) × 100",
          values: (kpis) =>
            `(${kpis[excludedKey]} / (${kpis[validKey]} + ${kpis[invalidKey]} + ${kpis[excludedKey]})) * 100`,
          result: (kpi) => `${formatNum(kpi.excludedPctOfTotal)}%`,
        },
      ],
    },

    accuracyPercentage: {
      title: "Accuracy %",
      sections: [
        {
          label: "Accuracy %",
          formula:
            "(Processed Records / (Processed Records + Exceptions)) × 100",
          values: (kpis) =>
            `(${kpis[validKey]} / (${kpis[validKey]} + ${kpis[invalidKey]})) * 100`,
          result: (kpi) => `${formatNum(kpi.accuracyPct)}%`,
        },
      ],
    },

    errorPercentage: {
      title: "Error %",
      sections: [
        {
          label: "Overall Error Rate",
          formula: "(Exceptions / (Processed Records + Exceptions)) × 100",
          values: (kpis) =>
            `(${kpis[invalidKey]} / (${kpis[validKey]} + ${kpis[invalidKey]})) * 100`,
          result: (kpi) => `${formatNum(kpi.errorPct)}%`,
        },
      ],
    },
    duplicateRecords: {
      title: "Duplicate",
      sections: [
        {
          label: "Orders",
          formula: "Duplicate",
          values: (kpis) => `${kpis[invalidKey]}`,
          result: (kpi) => formatNum(kpi[invalidKey]),
        },
        {
          label: "% of Total",
          formula:
            "(Exceptions / (Processed Records + Exceptions + Excluded Records)) × 100",
          values: (kpis) =>
            `(${kpis[invalidKey]} / (${kpis[validKey]} + ${kpis[invalidKey]} + ${kpis[excludedKey]})) * 100`,
          result: (kpi) => `${formatNum(kpi.invalidPctOfTotal)}%`,
        },
      ],
    },
  };

  const config = KPI_CALCULATION_MAP[kpiKey];

  if (!config) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          KPI Information
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            No calculation information available for this KPI.
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      disableScrollLock
      onClose={onClose}
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 4,
        },
      }}
      fullWidth
      className="kpi-dialog"
    >
      <DialogTitle sx={{ pr: 5 }}>
        {config.title}
        <IconButton
          onClick={onClose}
          className="kpi-close-btn"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {config.sections.map((section, id) => (
          <Box key={section.label} className="kpi-section">
            {/* Hide label of first section */}
            {id !== 0 && (
              <Typography className="kpi-section-title">
                {section.label}
              </Typography>
            )}
            <Typography className="kpi-formula">{section.formula}</Typography>

            <Box className="kpi-equation">
              <span>{section.values(kpiData)}</span>
            </Box>

            <div className="kpi-result">= {section.result(kpiData)}</div>

            {id < config.sections.length - 1 && (
              <Divider className="kpi-divider" />
            )}
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};
